/**
 * The active book — which book the screens read.
 *
 * Bright on Wheels and its TVS business are two books with the same mitrs and
 * outlets (owner, 8 Sep 2026): a staff session signed into one can flip to
 * the other from the header. A head-office sign-in (`allBooks`, later the
 * same day: "Surbhi and Abhishek should sign in like the head admin, every
 * portal") can flip to any of the four. Every request then carries `X-Book`
 * and the server answers from that book as the same person. The choice is
 * kept per browser. A counter sign-in never flips.
 */
import { useSyncExternalStore } from 'react';
import { isCoverLive, isGuardRole, isStaffRole, purchaseCheckBooks, type SessionCover } from '@bright/shared';

import { isCounterRole } from '@/lib/roles';

export type BookKey = 'autozone' | 'bow' | 'tvs' | 'fabev';

export const ALL_BOOKS: readonly BookKey[] = ['autozone', 'bow', 'tvs', 'fabev'];

const STORAGE_KEY = 'bsa.book';
const listeners = new Set<() => void>();
let active: BookKey | null = read();

function isBookKey(value: unknown): value is BookKey {
  return typeof value === 'string' && (ALL_BOOKS as readonly string[]).includes(value);
}

function read(): BookKey | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isBookKey(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** The book requests are served from, or null for the session's own. */
export function getActiveBook(): BookKey | null {
  return active;
}

export function setActiveBook(next: BookKey | null): void {
  active = next;
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, next);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode or storage disabled: the choice lasts the page.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useActiveBook(): BookKey | null {
  return useSyncExternalStore(subscribe, getActiveBook, () => null);
}

/**
 * The books whose purchase check this sign-in keeps AND can open (30 Oct 2026): the shared rule
 * says who checks a book; the book has to be one the header can reach — its own, or one it opens.
 */
export function checkableBooks(user: (BookUser & { email: string; organization?: { id: string } | null }) | null | undefined): string[] {
  if (!user) return [];
  const home = user.organization?.id ?? user.organizationId ?? '';
  const open = new Set<string>([home, ...booksFor(user).map((key) => BOOK_ORG[key])]);
  return purchaseCheckBooks({ email: user.email, organizationId: home, roleKey: user.role?.key }).filter((book) => open.has(book));
}

/** The organisation id a book key names. */
export const BOOK_ORG: Record<BookKey, string> = {
  autozone: 'org_bright_autozone_wholesale',
  bow: 'org_bright_on_wheels',
  tvs: 'org_bow_tvs',
  fabev: 'org_fab_ev',
};

export const BOOK_LABEL: Record<BookKey, string> = {
  autozone: 'Autozone',
  bow: 'Bright on Wheels',
  tvs: 'TVS',
  fabev: 'FAB EV',
};

/** The maker's book — dispatches and buyers, no store of its own to sell from. */
export const BOOK_WHOLESALE: Record<BookKey, boolean> = {
  autozone: true,
  bow: false,
  tvs: false,
  fabev: false,
};

/** The book key for an organisation, else null. */
export function bookKeyFor(organizationId: string | null | undefined): BookKey | null {
  for (const key of ALL_BOOKS) if (BOOK_ORG[key] === organizationId) return key;
  return null;
}

type BookUser = { allBooks?: boolean; organizationId?: string; role?: { key: string }; cover?: Pick<SessionCover, 'book' | 'untilAt'> | null };

/** A cover the session carries, while its time has not passed — a lapsed one stops showing by itself. */
export function liveCover<T extends Pick<SessionCover, 'untilAt'>>(cover: T | null | undefined): T | null {
  return cover && isCoverLive({ endedAt: null, untilAt: cover.untilAt }) ? cover : null;
}

/** The books a session may flip to from the header — none for a counter. */
export function booksFor(user: BookUser | null | undefined): BookKey[] {
  const own = ownBooksFor(user);
  // A cover of someone's work in another book opens that book too, while it lasts (20 Oct 2026).
  const covered = liveCover(user?.cover)?.book;
  if (!user || !isBookKey(covered) || isCounterRole(user.role?.key)) return own;
  const home = bookKeyFor(user.organizationId);
  const list = own.length > 0 ? own : home ? [home] : [];
  return list.includes(covered) ? list : [...list, covered];
}

function ownBooksFor(user: BookUser | null | undefined): BookKey[] {
  if (!user || isCounterRole(user.role?.key)) return [];
  // Staff work in no book — attendance, tasks and expenses are the person's (14 Oct 2026).
  // Nor do the guards: the gate register is the factory's, one for every book (23 Oct 2026).
  if (isStaffRole(user.role?.key) || isGuardRole(user.role?.key)) return [];
  if (user.allBooks) return [...ALL_BOOKS];
  // Bright on Wheels opens its TVS line; a TVS login stays in TVS (its staff
  // are TVS-only, 8 Sep 2026) — the server refuses the other way regardless.
  return bookKeyFor(user.organizationId) === 'bow' ? ['bow', 'tvs'] : [];
}

/**
 * The cover this session is working under in the book on screen, else null
 * (owner, 20 Oct 2026: "i want sunil to transfer vehicles in factory today
 * till the time amit is back"). In the covered person's book the screens show
 * their work: their rights, and — where the cover is the only way in — their
 * rail and home in place of one's own.
 */
export function coverInUse(user: (BookUser & { cover?: SessionCover | null }) | null | undefined, activeBook: BookKey | null): SessionCover | null {
  const cover = liveCover(user?.cover);
  if (!cover?.book) return null;
  const inUse = activeBook && booksFor(user).includes(activeBook) ? activeBook : bookKeyFor(user?.organizationId);
  return inUse === cover.book ? cover : null;
}
